import { usePositionStore } from "@/store/pinia/position";
import { getCurrentInstance } from "vue";
import { usePositionFilters } from "@/composables/positions/usePositionFilters";
import { computed, nextTick } from "vue";
import {
  attachRoleToPosition,
  deActivatePosition,
  reActivatePosition,
  retirePosition,
  removePositionAssignmentForEmployee,
  archivePosition,
  unArchivePosition,
  deletePosition as deletePositionAPI,
  updatePosition as updatePositionAPI,
  createPosition as createPositionAPI,
  activatePosition,
  getListOfRoles,
  getListofResponsibility,
  createRole,
  getChatterList,
  updatePositionExcludeValue,
  getRolesAssignedToPosition,
} from "@/api";

export function usePositions() {
  const positionStore = usePositionStore();
  const app = getCurrentInstance();
  const { proxy } = app || {};
  const {
    // finalQueryParams,
    setPaginationLimit,
    setPaginationOffset,
    setOrderingField,
    addFilter,
    removeFilter,
    clearDynamicFilters,
    dynamicFilters,
    defaultParams,
  } = usePositionFilters();
  const allPositions = computed(() => positionStore.getPositions);

  const POSITIONS_ORDERING_KEY = "positions.ordering";
  try {
    const savedOrdering =
      typeof localStorage !== "undefined" &&
      localStorage.getItem(POSITIONS_ORDERING_KEY);
    if (savedOrdering) {
      setOrderingField(savedOrdering);
    }
  } catch (e) {
    /* ignore storage errors */
  }

  const operations = {
    CREATE_POSITION: "CREATE_POSITION",
    UPDATE_POSITION: "UPDATE_POSITION",
    DELETE_POSITION: "DELETE_POSITION",
    ACTIVATE_POSITION: "ACTIVATE_POSITION",
    DEACTIVATE_POSITION: "DEACTIVATE_POSITION",
    REACTIVATE_POSITION: "REACTIVATE_POSITION",
    RETIRE_POSITION: "RETIRE_POSITION",
    ARCHIVE_POSITION: "ARCHIVE_POSITION",
    RENAME_POSITION: "RENAME_POSITION",
    ATTACH_ROLE: "ATTACH_ROLE",
    RELIEVE_ASSIGNMENT: "RELIEVE_ASSIGNMENT",
    CLONE_POSITION: "CLONE_POSITION",
  };

  const loadAllPositions = async () => {
    proxy.$loader.show();
    await positionStore.loadPositions({
      ...defaultParams.value,
      ...dynamicFilters.value,
    });
    proxy.$loader.hide();
    await nextTick();
  };

  const loadAllPositionsByBatches = async () => {
    await positionStore.loadPositionsBatch();
    positionStore.fetchRemainingPositionsInBatches();
  };

  const createPosition = async ({ accountId, name }) => {
    const payload = {
      name,
      account: accountId,
    };
    const { data } = await createPositionAPI(accountId, payload);
    return data;
  };

  const getRolesAttachedToPosition = async (positionId) => {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const { data } = await getRolesAssignedToPosition(accountId, positionId);
      return data?.results || [];
    } catch (e) {
      console.error(e);
    }
  };

  const updatePosition = async ({ accountId, positionId, payload }) => {
    const response = await updatePositionAPI(accountId, positionId, payload);
    return response.data;
  };

  const deletePosition = async ({ accountId, position }) => {
    await deletePositionAPI(accountId, position.id);
  };

  const activate = async ({ accountId, position }) => {
    await activatePosition(accountId, position.id);
  };

  const deactivate = async ({ accountId, position }) => {
    await deActivatePosition(accountId, position.id);
  };

  const reactivate = async ({ accountId, position }) => {
    await reActivatePosition(accountId, position.id);
  };

  const retire = async ({ accountId, position }) => {
    await retirePosition(accountId, position.id);
  };

  const archive = async ({ accountId, position }) => {
    await archivePosition(accountId, "position", position.id);
  };

  const unarchive = async ({ accountId, position }) => {
    await unArchivePosition(accountId, "position", position.id);
  };

  const setPaginationDataLimit = (limit) => {
    setPaginationLimit(limit);
  };

  const paginationDefaultParams = computed(() => defaultParams.value);

  const paginationLimit = computed(() => defaultParams.value.limit);
  const paginationOffset = computed(() => defaultParams.value.offset);
  const paginationOrdering = computed(() => defaultParams.value.ordering);

  const setPaginationDataOffset = (offset) => {
    setPaginationOffset(offset);
  };

  const setOrderingDataField = (ordering) => {
    setOrderingField(ordering);
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(POSITIONS_ORDERING_KEY, ordering);
      }
    } catch (e) {
      /* ignore storage errors */
    }
  };
  const addPositionFilter = (key, value) => {
    addFilter(key, value);
  };

  const removePositionFilter = (key) => {
    removeFilter(key);
  };

  const getDynamicPositionFilters = () => {
    return dynamicFilters.value;
  };

  const clearPositionDynamicFilters = () => {
    clearDynamicFilters();
  };

  const clonePosition = async ({ accountId, position }) => {
    try {
      const name = position?.name;
      const payload = {
        name: "Clone of " + name,
        account: accountId,
      };
      const { data } = await createPositionAPI(accountId, payload);
      return data;
    } catch (e) {
      console.log("e: ", e);
    }
  };

  const attachRole = async ({ accountId, positionId, roleId }) => {
    const payload = {
      attachment_id: roleId,
    };
    const response = await attachRoleToPosition(accountId, positionId, payload);
    return response.data;
  };

  const relievePrimaryAssignment = async ({ accountId, assignmentId }) => {
    await removePositionAssignmentForEmployee(accountId, assignmentId);
  };

  const rename = async ({ accountId, position, newName }) => {
    const payload = {
      name: newName,
      account: accountId,
    };
    await updatePositionAPI(accountId, position.id, payload);
  };

  const updateCapacity = async ({ accountId, positionId, capacity, name }) => {
    const payload = {
      name,
      account: accountId,
      capacity,
    };
    await updatePositionAPI(accountId, positionId, payload);
  };

  const setCurrentPosition = (position) => {
    positionStore.setCurrentPosition(position);
  };

  const getCurrentPosition = () => {
    return positionStore.getCurrentPosition;
  };

  const getPositionsCount = () => {
    return positionStore.getTotalPositionsCount;
  };

  const getRolesById = async ({ accountId }) => {
    const response = await getListOfRoles(accountId, 1, 500);
    return response.data.results;
  };

  const getResponsibilityByAttachRoles = async ({ accountId }) => {
    const response = await getListofResponsibility(accountId, 1, 500);
    return response.data.results;
  };

  const createAttachRole = async ({ accountId, role }) => {
    const data = await createRole(accountId, role);
    return data.data;
  };
  const positionChatter = async (accountID, position_id, type, page) => {
    try {
      const response = await getChatterList(accountID, position_id, type, page);
      return response;
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const updatePositionExcludeFromAvg = async (accountID, data) => {
    try {
      const payload = {
        exclude_from_avg: data.value,
      };
      await updatePositionExcludeValue(accountID, data.position_id, payload);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  /**
   * Get avatar with fallback logic
   * First tries assigned_employee_avatar, then falls back to occupied_by/avatar
   * @param {Object} employee - Employee object containing avatar fields
   * @returns {string} Avatar URL or empty string
   */
  const getEmployeeAvatar = (employee) => {
    if (!employee) return "";

    // First try assigned_employee_avatar
    if (employee.assigned_employee_avatar) {
      return employee.assigned_employee_avatar.startsWith("http")
        ? employee.assigned_employee_avatar
        : `https://d2ox6kgfo0vgc9.cloudfront.net/${employee.assigned_employee_avatar}`;
    }

    // Fallback to occupied_by/avatar
    if (employee.occupied_by?.avatar) {
      return employee.occupied_by.avatar.startsWith("http")
        ? employee.occupied_by.avatar
        : `https://d2ox6kgfo0vgc9.cloudfront.net/${employee.occupied_by.avatar}`;
    }

    return "";
  };

  return {
    allPositions,
    operations,
    loadAllPositions,
    createPosition,
    updatePosition,
    deletePosition,
    activate,
    deactivate,
    reactivate,
    retire,
    archive,
    unarchive,
    clonePosition,
    attachRole,
    relievePrimaryAssignment,
    rename,
    updateCapacity,
    setCurrentPosition,
    getCurrentPosition,
    getRolesById,
    getResponsibilityByAttachRoles,
    createAttachRole,
    loadAllPositionsByBatches,
    positionChatter,
    updatePositionExcludeFromAvg,
    setPaginationDataLimit,
    setPaginationDataOffset,
    setOrderingDataField,
    addPositionFilter,
    removePositionFilter,
    clearPositionDynamicFilters,
    getDynamicPositionFilters,
    getPositionsCount,
    getRolesAttachedToPosition,
    paginationDefaultParams,
    paginationLimit,
    paginationOffset,
    paginationOrdering,
    getEmployeeAvatar,
  };
}
